import { Injectable } from '@angular/core';
import {
  FzGoogleTagManagerService,
  PromoService,
} from '@fantaz/shared/data-access/api';
import { GAEventKeys, PromoCode } from '@fantaz/shared/data-access/models';
import { ToastService } from '@fantaz/shared/utils/toast';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  concatMap,
  map,
  of,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';

import { getCurrentUser } from '@fantaz/shared/auth';
import { Store } from '@ngrx/store';
import { applyPromoCodeSuccess } from '../+state/promo-code/promo-code.actions';
import * as PromoCodeActions from './promo-code.actions';

@Injectable()
export class PromoCodeEffects {
  init$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PromoCodeActions.loadPromoteCodes),
        switchMap(({ params }) => {
          return this.promoService.loadPromoCodes(params).pipe(
            map(({ items, total }) => {
              return PromoCodeActions.loadPromoCodesSuccess({
                promoCodes: items,
                total,
              });
            }),
            catchError((error) => {
              return of(PromoCodeActions.loadPromoCodesFailure({ error }));
            }),
          );
        }),
      ),
    { dispatch: true },
  );

  /**
   * This effect is used to refresh the promo code list after a promo code is
   * applied
   */
  refresh$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(applyPromoCodeSuccess),
        withLatestFrom(this.store.select(getCurrentUser), (action, user) => ({
          action,
          user,
        })),
        switchMap((data) => {
          if (data?.user) {
            return this.promoService.loadPromoCodes({}).pipe(
              map(({ items, total }) => {
                return PromoCodeActions.loadPromoCodesSuccess({
                  promoCodes: items,
                  total,
                });
              }),
              catchError((error) => {
                return of(PromoCodeActions.loadPromoCodesFailure({ error }));
              }),
            );
          }

          return of();
        }),
      ),
    { dispatch: true },
  );

  confirm$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PromoCodeActions.confirmPromoCode),
        concatMap(({ promoCodeId, reload }) => {
          return this.promoService.confirmPromoCode(promoCodeId).pipe(
            map((data) => {
              // this.promoService.removeFromLocalStorage(promoCodeId);
              return PromoCodeActions.confirmPromoCodeSuccess({
                promoCode: data,
                code: promoCodeId,
                options: { reload },
              });
            }),
            catchError((error) => {
              return of(
                PromoCodeActions.confirmPromoCodeFailure({
                  error,
                  promoCode: promoCodeId,
                }),
              );
            }),
          );
        }),
      ),
    { dispatch: true },
  );
  confirmPromoCodeSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PromoCodeActions.confirmPromoCodeSuccess),
        switchMap(({ promoCode, options }) => {
          const message = `Well done! The promo code "${promoCode.uuid}" is awesome.`;
          this.toastService.openSnackBar(message);
          const actions = [];
          if (options?.reload) {
            actions.push(PromoCodeActions.loadPromoteCodes);
          }
          actions.push(
            PromoCodeActions.sendPromoCodeGA({
              promoCode,
              onboarding: options?.onboarding,
              sponsor: options?.sponsor,
            }),
          );
          return actions;
        }),
      ),
    { dispatch: true },
  );

  // !FIXME: Apply SOLID PRINCIPLES HERE, this effect can be used only to report promo code to google analytics
  sendPromoCodeGACampaign$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PromoCodeActions.sendPromoCodeGA),
        withLatestFrom(this.store.select(getCurrentUser)),
        tap(([{ promoCode, onboarding, sponsor, options }, currentUser]) => {
          this.googleTagManager.pushTag({
            event: GAEventKeys.PROMO_CODE,
            promo_code_action: 'onboarding_confirmed_promo_code',
            promo_code: promoCode.uuid,
            onboarding,
            user_id: currentUser?.user.uuid,
            user_name: currentUser?.user.username,
            sponsor: currentUser?.user.sponsorUsername || sponsor,
          });
          if (promoCode.urlLandingPage) {
            // this is a temporal fix to avoid redirecting to landing page when
            // the user is onboarding
            if (!(options && options.disabledRedirect)) {
              this.redirectionToLandingPage(promoCode);
            }
          } else {
            const url = new URL(window.location.href);

            if (!(options && options.disabledRedirect)) {
              this.getRedirectionUrl(promoCode, url);
            }

            this.googleTagManager.pushTag({
              event: GAEventKeys.PAGE_VIEW,
              hostname: window.location.hostname,
              url: url.href,
              path: '/',
              user_id: currentUser?.user.uuid,
              user_name: currentUser?.user.username,
              page_title: 'Promo code associated',
              promo_code: promoCode.uuid,
            });
          }
        }),
      ),
    { dispatch: false },
  );
  confirmPromoCodeFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PromoCodeActions.confirmPromoCodeFailure),
        tap(({ error, promoCode }) => {
          console.log(error);
          const message = `The promo code "${promoCode}" does not exist in our records, or it's already expired.`;
          this.toastService.openSnackBar(message);
        }),
      ),
    { dispatch: false },
  );

  private redirectionToLandingPage(promoCode: PromoCode) {
    const url = new URL(promoCode.urlLandingPage);
    this.getRedirectionUrl(promoCode, url);
    const message = `Well done! The promo code "${promoCode.uuid}" is awesome, we will redirect you now…`;
    this.toastService.openSnackBar(message);
    setTimeout(() => {
      window.location.href = url.href;
    }, 2000);
    return message;
  }

  private getRedirectionUrl(promoCode: PromoCode, url: URL) {
    const utm_source = promoCode.utm?.utm_source
      ? promoCode.utm?.utm_source
      : promoCode.uuid;
    const utm_medium = promoCode.utm?.utm_medium
      ? promoCode.utm?.utm_medium
      : promoCode.uuid;
    const utm_campaign = promoCode.utm?.utm_campaign
      ? promoCode.utm?.utm_campaign
      : promoCode.uuid;
    url.searchParams.append('utm_source', utm_source);
    url.searchParams.append('utm_medium', utm_medium);
    url.searchParams.append('utm_campaign', utm_campaign);
  }

  constructor(
    private readonly actions$: Actions,
    private googleTagManager: FzGoogleTagManagerService,
    private store: Store,
    private promoService: PromoService,
    private toastService: ToastService,
  ) {}
}
